import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';
import UiSelect from '../components/ui/Select';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || []);

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(ENDPOINTS.ADMIN.ORGANIZATIONS, { limit: 500 });
      setOrgs(toArr(data));
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter(o => {
      if (status !== 'all' && (o.status || 'active') !== status) return false;
      if (q) {
        const hay = [o.name, o.slug, o.contact_email, o.plan].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orgs, search, status]);

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Organizations</strong></div>
          <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
            <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Organizations</em>
          </h1>
          <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}><strong style={{ color: '#FAFAFA' }}>{filtered.length}</strong> of {orgs.length} tenants</p>
        </div>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input className="field-input" placeholder="Search name, slug, email…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
        <div style={{ width: 170 }}>
          <UiSelect value={status} onChange={setStatus} options={[
            { value: 'all', label: 'All statuses' }, { value: 'active', label: 'Active' },
            { value: 'trialing', label: 'Trialing' }, { value: 'suspended', label: 'Suspended' }, { value: 'cancelled', label: 'Cancelled' },
          ]} />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr><th>Organization</th><th>Plan</th><th>Users</th><th>Branches</th><th>Contact</th><th>Status</th><th>Joined</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No organizations found</td></tr>}
            {filtered.map(o => (
              <tr key={o.id} className="cursor-pointer" onClick={() => navigate(`/organizations/${o.id}`)}>
                <td>
                  <div className="flex flex-col gap-0.5">
                    <span className="tbl-id">{o.name}</span>
                    {o.slug && <span className="font-mono text-[11px] text-muted">{o.slug}</span>}
                  </div>
                </td>
                <td style={{ color: '#D4D4D8' }}>{o.plan || o.plan_name || '-'}</td>
                <td className="tbl-mono">{o.user_count ?? '-'}</td>
                <td className="tbl-mono">{o.branch_count ?? '-'}</td>
                <td style={{ color: '#A1A1AA', fontSize: 12 }}>{o.contact_email || '-'}</td>
                <td><StatusPill status={(o.status || 'active') === 'active' ? 'active' : 'on_hold'} label={(o.status || 'active').replace(/^\w/, c => c.toUpperCase())} /></td>
                <td className="tbl-mono">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
